import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import ReactMarkdown from "react-markdown";
import {
    Sparkles,
    Send,
    Bot,
    ChevronRight,
    ChevronLeft,
    Loader2,
    Plus,
    Coins,
    X,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { apiFetch, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * AI Chat panel — Phase 6B, streaming via SSE.
 *
 * Uses fetch + ReadableStream reader because EventSource cannot POST a
 * body. Each chat message consumes 1 credit atomically on the backend.
 */
export default function AIPanel({ open, onToggle }) {
    const { t } = useTranslation();
    const { user, refresh } = useAuth();
    const [sessionId, setSessionId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [streaming, setStreaming] = useState(false);
    const [assistantDraft, setAssistantDraft] = useState("");
    const scrollRef = useRef(null);
    const abortRef = useRef(null);

    const scrollBottom = useCallback(() => {
        requestAnimationFrame(() => {
            if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        });
    }, []);

    // Auto-create a session on first open
    useEffect(() => {
        if (!open || sessionId) return;
        (async () => {
            try {
                const res = await apiFetch("/ai/chat/sessions", { method: "POST" });
                setSessionId(res.session.id);
            } catch (e) {
                // fail silently — chat unavailable until user retries
            }
        })();
    }, [open, sessionId]);

    useEffect(() => { scrollBottom(); }, [messages, assistantDraft, scrollBottom]);

    useEffect(() => () => abortRef.current?.abort(), []);

    const startNewChat = async () => {
        try {
            const res = await apiFetch("/ai/chat/sessions", { method: "POST" });
            setSessionId(res.session.id);
            setMessages([]);
            setAssistantDraft("");
        } catch (e) {
            toast.error(e.message);
        }
    };

    const sendMessage = async () => {
        const prompt = input.trim();
        if (!prompt || streaming || !sessionId) return;
        setInput("");
        setMessages((m) => [...m, { role: "user", content: prompt, id: `local-${Date.now()}` }]);
        setStreaming(true);
        setAssistantDraft("");

        const controller = new AbortController();
        abortRef.current = controller;
        try {
            const res = await fetch(`${API}/ai/chat/sessions/${sessionId}/messages`, {
                method: "POST",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "text/event-stream",
                },
                body: JSON.stringify({
                    content: prompt,
                    context: {
                        plan_id: user?.plan_id,
                        credits_remaining: user?.credits,
                        current_page: window.location.pathname,
                    },
                }),
                signal: controller.signal,
            });

            if (!res.ok || !res.body) {
                const errText = await res.text().catch(() => "");
                throw new Error(errText || `HTTP ${res.status}`);
            }

            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";
            let acc = "";

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                let idx;
                while ((idx = buffer.indexOf("\n\n")) !== -1) {
                    const block = buffer.slice(0, idx);
                    buffer = buffer.slice(idx + 2);
                    for (const line of block.split("\n")) {
                        if (!line.startsWith("data: ")) continue;
                        const payload = line.slice(6);
                        if (payload === "[DONE]") continue;
                        let evt;
                        try { evt = JSON.parse(payload); } catch { continue; }
                        if (evt.event === "delta") {
                            acc += evt.content || "";
                            setAssistantDraft(acc);
                        } else if (evt.event === "meta") {
                            // credit balance updated — refresh header
                            refresh && refresh();
                        } else if (evt.event === "error") {
                            const detail = evt.detail || "chat_error";
                            if (detail === "rate_limit_exceeded") {
                                toast.error(t("chat.rate_limited", { n: Math.ceil((evt.retry_after || 60) / 60) }));
                            } else if (detail === "InsufficientCredits") {
                                toast.error(t("chat.insufficient_credits"));
                            } else if (detail === "HourlyCapReached") {
                                toast.error("Hourly credit cap reached — try again in an hour.");
                            } else {
                                toast.error(`Chat error: ${detail}`);
                            }
                            setAssistantDraft("");
                            setStreaming(false);
                            return;
                        } else if (evt.event === "done") {
                            setMessages((m) => [
                                ...m,
                                { role: "assistant", content: acc, id: evt.message_id },
                            ]);
                            setAssistantDraft("");
                            setStreaming(false);
                            return;
                        }
                    }
                }
            }
        } catch (e) {
            if (e.name !== "AbortError") toast.error(e.message || "Chat error");
        } finally {
            setStreaming(false);
        }
    };

    const onKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    return (
        <>
            {/* Hotfix AI-floating · FAB fisso bottom-right (tutti i breakpoint) · V12 Achromatic Strict */}
            {!open && (
                <button
                    data-testid="ai-panel-toggle"
                    onClick={onToggle}
                    className="fixed right-6 bottom-6 z-50 h-14 w-14 rounded-full border border-[#1A1A1A] bg-black grid place-items-center shadow-[0_8px_32px_rgba(0,0,0,0.6)] hover:scale-105 hover:border-white/40 transition-all duration-200"
                    aria-label="Open AI assistant"
                >
                    <Sparkles className="h-5 w-5 text-white" />
                    <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-[var(--color-success)] ring-2 ring-black" />
                </button>
            )}

            <AnimatePresence initial={false}>
                {open && (
                    <motion.aside
                        data-testid="ai-chat-panel"
                        initial={{ y: 20, opacity: 0, scale: 0.96 }}
                        animate={{ y: 0, opacity: 1, scale: 1 }}
                        exit={{ y: 20, opacity: 0, scale: 0.96 }}
                        transition={{ duration: 0.22, ease: "easeOut" }}
                        className="fixed right-6 bottom-6 z-50 flex flex-col w-[calc(100vw-3rem)] sm:w-96 h-[min(600px,calc(100vh-3rem))] rounded-2xl border border-[#1A1A1A] bg-[#0A0A0A] shadow-[0_24px_64px_rgba(0,0,0,0.8)] overflow-hidden"
                    >
                        <div className="flex items-center justify-between px-5 py-4 border-b border-[#1A1A1A]">
                            <div className="flex items-center gap-2">
                                <span className="relative h-8 w-8 rounded-lg bg-black grid place-items-center border border-[#1A1A1A]">
                                    <Sparkles className="h-4 w-4 text-white" />
                                    <span className="absolute -bottom-0.5 -right-0.5 h-2 w-2 rounded-full bg-[var(--color-success)] ring-2 ring-[#0A0A0A]" />
                                </span>
                                <div>
                                    <p className="text-sm font-semibold text-white">{t("chat.title")}</p>
                                    <p className="text-[11px] text-[var(--color-success)]">{t("chat.online")}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    data-testid="ai-chat-new-chat"
                                    onClick={startNewChat}
                                    className="text-neutral-400 hover:text-white transition-colors"
                                    aria-label="New chat"
                                    title="New chat"
                                >
                                    <Plus className="h-4 w-4" />
                                </button>
                                <button
                                    data-testid="ai-panel-close"
                                    onClick={onToggle}
                                    className="text-neutral-400 hover:text-white transition-colors"
                                    aria-label="Close AI panel"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            </div>
                        </div>

                        <div
                            ref={scrollRef}
                            className="flex-1 overflow-y-auto p-4 space-y-3"
                            data-testid="ai-chat-message-list"
                        >
                            {messages.length === 0 && !assistantDraft && (
                                <div className="rounded-xl border border-dashed border-[#1A1A1A] bg-transparent p-4 text-center">
                                    <p className="text-xs text-neutral-400">
                                        Ask about lead generation strategy, pricing plans, or product features.
                                    </p>
                                </div>
                            )}

                            {messages.map((m) => (
                                <MessageBubble key={m.id} role={m.role} content={m.content} />
                            ))}
                            {assistantDraft && (
                                <MessageBubble role="assistant" content={assistantDraft} streaming />
                            )}
                            {streaming && !assistantDraft && (
                                <div className="flex items-center gap-2 text-xs text-neutral-400">
                                    <Loader2 className="h-3 w-3 animate-spin" /> Thinking…
                                </div>
                            )}
                        </div>

                        <div className="p-3 border-t border-[#1A1A1A] space-y-2">
                            <div className="flex gap-2 items-end">
                                <textarea
                                    data-testid="ai-chat-input"
                                    rows={2}
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    onKeyDown={onKeyDown}
                                    disabled={streaming}
                                    placeholder={t("chat.input_placeholder")}
                                    className="flex-1 resize-none rounded-lg border border-[#1A1A1A] bg-black px-3 py-2 text-sm text-white placeholder:text-neutral-500 focus:border-white/40 focus:outline-none disabled:opacity-60"
                                />
                                <button
                                    data-testid="ai-chat-send"
                                    onClick={sendMessage}
                                    disabled={streaming || !input.trim()}
                                    className="h-9 w-9 shrink-0 rounded-lg bg-white hover:bg-[#EDEDED] grid place-items-center text-black disabled:opacity-40"
                                    aria-label="Send"
                                >
                                    {streaming ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                        <Send className="h-4 w-4" />
                                    )}
                                </button>
                            </div>
                            <div className="flex items-center gap-1.5 text-[10px] text-neutral-500">
                                <Coins className="h-3 w-3" />
                                {t("chat.credit_hint")}
                            </div>
                        </div>
                    </motion.aside>
                )}
            </AnimatePresence>
        </>
    );
}

function MessageBubble({ role, content, streaming }) {
    const isUser = role === "user";
    return (
        <div
            data-testid={`ai-chat-message-${role}`}
            className={`flex ${isUser ? "justify-end" : "justify-start"}`}
        >
            <div
                className={`max-w-[85%] rounded-xl px-3 py-2 ${
                    isUser
                        ? "bg-[#1F1F1F] text-white"
                        : "bg-[var(--color-card)] border border-[var(--color-border)]"
                }`}
            >
                {isUser ? (
                    <p className="text-sm whitespace-pre-wrap">{content}</p>
                ) : (
                    <div className="prose prose-invert prose-sm max-w-none prose-p:my-1 prose-headings:my-2 prose-headings:text-text-main prose-p:text-text-main prose-strong:text-text-main prose-code:text-[var(--color-secondary)] prose-code:bg-transparent prose-ul:my-1">
                        <ReactMarkdown>{content}</ReactMarkdown>
                        {streaming && (
                            <span className="inline-block h-3 w-2 bg-[var(--color-secondary)] ml-0.5 animate-pulse" />
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
